import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import ReactGA from "react-ga4";
import { AuthKitProvider } from "@workos-inc/authkit-react";

import AppShell from './AppShell.tsx';
import AccountLayout from './features/account/AccountLayout.tsx';
import ForgotPasswordPage from './features/account/ForgotPasswordPage.tsx';
// import LoginPage from './features/account/LoginPage.tsx';
import LoginRedirectPage from './features/account/LoginRedirectPage.tsx';
import ResetPasswordPage from './features/account/ResetPasswordPage.tsx';
// import SignupPage from './features/account/SignUpPage.tsx';
import SignupPage from './features/account/SignupRedirectPage.tsx';
import VerifyEmailPage from './features/account/VerifyEmailPage.tsx';
import { ACCOUNT_PATHS, ACCOUNT_ROOT, ACCOUNT_SEGMENTS } from './features/account/paths.ts';
import CallbackPage from './features/account/CallbackPage.tsx';
import AccountPage from './features/account/AccountPage.tsx';
import MeProvider from './features/account/MeProvider.tsx';
import OnboardingGate from './features/account/OnboardingGate.tsx';
import OnboardingPage from './features/account/OnboardingPage.tsx';
import SharedSchedulePage from './features/share/SharedSchedulePage.tsx';
import LegalPage from './features/legal/LegalPage.tsx';
import { PRIVACY_PATH, TERMS_PATH } from './features/account/legalLinks.ts';
import { gaPageLocation } from './features/share/gaLocation.ts';
import { AUTHKIT_OPTIONS } from './features/account/authConfig.ts';
import ThemeConfig from './shared/theme/index.tsx';
import { migrateLegacyIds } from './shared/legacyIds.ts';
import AccountDataProvider from './shared/account/AccountDataProvider.tsx';
import './shared/fonts.css';

// ReactGA.initialize("G-HHQSMQPQV8");
// Without a shared schedule's token or a login code in the address it reports.
ReactGA.initialize("G-HHQSMQPQV8", { gtagOptions: { page_location: gaPageLocation() } });

function render() {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ThemeConfig>
        {/* <AuthKitProvider clientId="client_01M29JB48BM5HHNWWQN0FB0P00"> */}
        {/* From the environment now, with that client as the fallback (authConfig.ts). */}
        <AuthKitProvider {...AUTHKIT_OPTIONS}>
          <BrowserRouter basename={import.meta.env.BASE_URL}>
            {/* The user's plans, from the device or their account, for every route. */}
            <AccountDataProvider>
            <MeProvider>
            <OnboardingGate />
            <Routes>
              {/* App stays mounted under the account pages, so the view toggle
                  never moves and coming back restores the view, the filters,
                  the sidebars and the scroll (AppShell). */}
              <Route element={<AppShell />}>
                <Route index element={null} />
                {/* The Account page (D3); it sends logged-out visitors to login
                    itself. Outside `AccountLayout` since Sept 19: it is a
                    full-bleed photograph with cards on it, not a form in that
                    layout's narrow column. */}
                <Route path={ACCOUNT_ROOT} element={<AccountPage />} />
                <Route path={ACCOUNT_ROOT} element={<AccountLayout />}>
                  {/* <Route index element={<Navigate to={ACCOUNT_SEGMENTS.logIn} replace />} /> */}
                  <Route path={ACCOUNT_SEGMENTS.signUp} element={<SignupPage />} />
                  <Route path={ACCOUNT_SEGMENTS.logIn} element={<LoginRedirectPage />} />
                  <Route path={ACCOUNT_SEGMENTS.verifyEmail} element={<VerifyEmailPage />} />
                  <Route path={ACCOUNT_SEGMENTS.forgotPassword} element={<ForgotPasswordPage />} />
                  <Route path={ACCOUNT_SEGMENTS.resetPassword} element={<ResetPasswordPage />} />
                  <Route path={ACCOUNT_SEGMENTS.callback} element={<CallbackPage />} />
                </Route>
              </Route>
              {/* Its own full-page card, outside the account column: that
                  column's "Back to My Climate Week" would only bounce off the
                  onboarding gate. */}
              <Route path={ACCOUNT_PATHS.welcome} element={<OnboardingPage />} />
              {/* A shared schedule (D11): public, outside the onboarding gate. */}
              <Route path="/s/:token" element={<SharedSchedulePage />} />
              {/* The two legal documents. Outside the account layout and the
                  onboarding gate: the sign-up card links to them in a new tab,
                  and they have to be readable signed out. */}
              <Route path={TERMS_PATH} element={<LegalPage document="terms" />} />
              <Route path={PRIVACY_PATH} element={<LegalPage document="privacy" />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </MeProvider>
            </AccountDataProvider>
          </BrowserRouter>
        </AuthKitProvider>
      </ThemeConfig>
    </StrictMode>,
  );
}

/**
 * Stored plans move to the backend's ids before anything reads them. A browser
 * with nothing to move renders at once; one fetching the id map waits a few
 * seconds at most, and renders whether or not the map arrived.
 */
void migrateLegacyIds().finally(render);
